import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../auth/AuthProvider';
import { supabase } from '../../../lib/supabaseClient';
import ImageUploadZone from '../../proof-submission/components/ImageUploadZone';
import { compressImageUnderBytes } from '../../../utils/imageCompression';
import { makeSafeObjectPath, uploadToBucket } from '../../../utils/storage';

const AccountSettings = () => {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    bio: '',
  });

  const [avatarImage, setAvatarImage] = useState(null);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    pushNotifications: false,
    weeklyReport: true,
  });

  const [errors, setErrors] = useState({});

  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');

  useEffect(() => {
    if (!user?.id) return;

    const load = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('ch_profiles')
          .select('id, username, bio, notification_prefs, avatar_url')
          .eq('id', user.id)
          .maybeSingle();

        if (error) return;

        setProfileData({
          name: data?.username || profile?.username || '',
          email: user?.email || '',
          bio: data?.bio || '',
        });

        setAvatarImage(data?.avatar_url ? { file: null, preview: data.avatar_url } : null);

        const prefs = data?.notification_prefs || {};
        setNotifications({
          emailNotifications: prefs?.emailNotifications ?? true,
          pushNotifications: prefs?.pushNotifications ?? false,
          weeklyReport: prefs?.weeklyReport ?? true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [profile?.username, user?.email, user?.id]);

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNotificationChange = (field, checked) => {
    setNotifications(prev => ({ ...prev, [field]: checked }));
  };

  const handleAvatarSelect = (image, error) => {
    if (!isEditing) return;
    setAvatarImage(image);
    if (error) {
      setErrors((prev) => ({ ...prev, avatar: error }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.avatar;
        return next;
      });
    }
  };

  const validateProfile = () => {
    const newErrors = {};
    if (!profileData?.name?.trim()) {
      newErrors.name = '이름을 입력해주세요';
    }
    if (!profileData?.email?.trim()) {
      newErrors.email = '이메일을 입력해주세요';
    } else if (!/\S+@\S+\.\S+/?.test(profileData?.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!passwordData?.currentPassword) {
      newErrors.currentPassword = '현재 비밀번호를 입력해주세요';
    }
    if (!passwordData?.newPassword) {
      newErrors.newPassword = '새 비밀번호를 입력해주세요';
    } else if (passwordData?.newPassword?.length < 8) {
      newErrors.newPassword = '비밀번호는 최소 8자 이상이어야 합니다';
    }
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSaveProfile = () => {
    const run = async () => {
      if (!user?.id) return;
      if (!validateProfile()) return;

      setIsLoading(true);
      try {
        const nextPrefs = {
          emailNotifications: !!notifications?.emailNotifications,
          pushNotifications: !!notifications?.pushNotifications,
          weeklyReport: !!notifications?.weeklyReport,
        };

        let avatarUrlToSave = avatarImage?.preview || null;
        if (avatarImage?.file) {
          const file = avatarImage.file;
          let blob = file;
          if (file.size > 1024 * 1024) {
            blob = await compressImageUnderBytes(file, 1024 * 1024);
          }

          const path = makeSafeObjectPath({ userId: user.id, originalName: file?.name, prefix: 'avatars' });
          const { error: uploadError } = await uploadToBucket({
            bucket: 'profile-avatars',
            path,
            blob,
            contentType: blob?.type || 'image/jpeg',
            cacheControl: '31536000',
          });

          if (!uploadError) {
            const { data: publicUrlData } = supabase.storage.from('profile-avatars').getPublicUrl(path);
            avatarUrlToSave = publicUrlData?.publicUrl || null;
          }
        }

        const { error: profileErr } = await supabase
          .from('ch_profiles')
          .update({
            username: profileData?.name?.trim(),
            bio: profileData?.bio || null,
            notification_prefs: nextPrefs,
            avatar_url: avatarUrlToSave,
          })
          .eq('id', user.id);

        if (profileErr) return;

        if (profileData?.email?.trim() && profileData.email.trim() !== (user?.email || '')) {
          await supabase.auth.updateUser({ email: profileData.email.trim() });
        }

        setIsEditing(false);
        setSuccessMessage('프로필이 성공적으로 업데이트되었습니다');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } finally {
        setIsLoading(false);
      }
    };

    run();
  };

  const handleSavePassword = () => {
    const run = async () => {
      if (!validatePassword()) return;
      setIsLoading(true);
      try {
        const email = user?.email || '';
        if (!email) {
          setErrors((prev) => ({ ...prev, currentPassword: '재인증을 위해 이메일 정보를 불러올 수 없습니다' }));
          return;
        }

        const { error: reauthError } = await supabase.auth.signInWithPassword({
          email,
          password: passwordData?.currentPassword,
        });

        if (reauthError) {
          setErrors((prev) => ({ ...prev, currentPassword: '현재 비밀번호가 올바르지 않습니다' }));
          return;
        }

        const { error } = await supabase.auth.updateUser({ password: passwordData?.newPassword });
        if (error) {
          setErrors((prev) => ({ ...prev, newPassword: error.message }));
          return;
        }

        setShowPasswordChange(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setSuccessMessage('비밀번호가 성공적으로 변경되었습니다');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } finally {
        setIsLoading(false);
      }
    };

    run();
  };

  const handleSaveNotifications = () => {
    const run = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      try {
        const nextPrefs = {
          emailNotifications: !!notifications?.emailNotifications,
          pushNotifications: !!notifications?.pushNotifications,
          weeklyReport: !!notifications?.weeklyReport,
        };

        const { error } = await supabase
          .from('ch_profiles')
          .update({ notification_prefs: nextPrefs })
          .eq('id', user.id);

        if (error) return;

        setSuccessMessage('알림 설정이 저장되었습니다');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } finally {
        setIsLoading(false);
      }
    };

    run();
  };

  const handleLogout = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await signOut();
      navigate('/login-registration');
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (isDeletingAccount) return;
    setDeleteAccountError('');
    setIsDeletingAccount(true);
    try {
      const { error } = await supabase.rpc('ch_delete_my_account');
      if (error) {
        setDeleteAccountError(error?.message || '탈퇴에 실패했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      await signOut();
      navigate('/login-registration');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {showSuccess && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center space-x-3">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" />
          <span className="text-sm md:text-base text-success">{successMessage}</span>
        </div>
      )}
      <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            프로필 정보
          </h3>
          {!isEditing && (
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={() => setIsEditing(true)}
              disabled={isLoading}
            >
              수정
            </Button>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              프로필 이미지
            </label>
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-14 h-14 rounded-full bg-muted/40 border border-border overflow-hidden flex items-center justify-center">
                {avatarImage?.preview ? (
                  <img
                    src={avatarImage.preview}
                    alt="프로필 이미지"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon name="User" size={22} className="text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground caption">
                  원형 프로필 이미지로 표시됩니다
                </p>
              </div>
            </div>
            <div className={!isEditing ? 'opacity-70 pointer-events-none' : ''}>
              <ImageUploadZone
                onImageSelect={handleAvatarSelect}
                selectedImage={avatarImage}
                error={errors?.avatar}
              />
            </div>
          </div>

          <Input
            label="이름"
            type="text"
            value={profileData?.name}
            onChange={(e) => handleProfileChange('name', e?.target?.value)}
            disabled={!isEditing}
            error={errors?.name}
            required
          />
          <Input
            label="이메일"
            type="email"
            value={profileData?.email}
            onChange={(e) => handleProfileChange('email', e?.target?.value)}
            disabled={!isEditing}
            error={errors?.email}
            required
          />
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              소개
            </label>
            <textarea
              value={profileData?.bio}
              onChange={(e) => handleProfileChange('bio', e?.target?.value)}
              disabled={!isEditing}
              rows={3}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed resize-none"
            />
          </div>
          {isEditing && (
            <div className="flex items-center space-x-3 pt-2">
              <Button variant="default" onClick={handleSaveProfile} loading={isLoading}>
                저장
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setErrors({});
                }}
                disabled={isLoading}
              >
                취소
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            비밀번호 변경
          </h3>
          {!showPasswordChange && (
            <Button
              variant="outline"
              size="sm"
              iconName="Lock"
              iconPosition="left"
              onClick={() => setShowPasswordChange(true)}
              disabled={isLoading}
            >
              변경
            </Button>
          )}
        </div>
        {showPasswordChange ? (
          <div className="space-y-4">
            <Input
              label="현재 비밀번호"
              type="password"
              value={passwordData?.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e?.target?.value)}
              error={errors?.currentPassword}
              required
            />
            <Input
              label="새 비밀번호"
              type="password"
              value={passwordData?.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e?.target?.value)}
              error={errors?.newPassword}
              description="최소 8자 이상"
              required
            />
            <Input
              label="비밀번호 확인"
              type="password"
              value={passwordData?.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e?.target?.value)}
              error={errors?.confirmPassword}
              required
            />
            <div className="flex items-center space-x-3 pt-2">
              <Button variant="default" onClick={handleSavePassword} loading={isLoading}>
                변경
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasswordChange(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setErrors({});
                }}
                disabled={isLoading}
              >
                취소
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            보안을 위해 정기적으로 비밀번호를 변경해주세요
          </p>
        )}
      </div>
      <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4 md:mb-6">
          알림 설정
        </h3>
        <div className="p-4 bg-muted/30 rounded-md border border-border">
          <p className="text-sm text-muted-foreground caption">준비중</p>
        </div>
      </div>

      <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4 md:mb-6">
          계정
        </h3>
        <div className="space-y-3">
          <Button
            variant="outline"
            fullWidth
            iconName="LogOut"
            iconPosition="left"
            onClick={handleLogout}
            loading={isSigningOut}
          >
            로그아웃
          </Button>

          <Button
            variant="destructive"
            fullWidth
            iconName="UserX"
            iconPosition="left"
            onClick={() => setIsDeleteConfirmOpen(true)}
            disabled={isSigningOut || isDeletingAccount}
          >
            탈퇴
          </Button>

          <p className="text-xs text-muted-foreground caption">
            탈퇴 시 계정 및 관련 데이터가 삭제될 수 있으며, 이 작업은 되돌릴 수 없습니다.
          </p>
        </div>
      </div>

      {isDeleteConfirmOpen && (
        <div
          className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => {
            if (!isDeletingAccount) setIsDeleteConfirmOpen(false);
          }}
        >
          <div
            className="w-full max-w-md bg-card rounded-lg border border-border shadow-gaming-lg"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="text-base md:text-lg font-heading font-semibold text-foreground">탈퇴</div>
              <button
                type="button"
                className="px-3 py-2 rounded-md hover:bg-muted transition-gaming text-sm"
                onClick={() => {
                  if (!isDeletingAccount) setIsDeleteConfirmOpen(false);
                }}
              >
                닫기
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-muted-foreground caption">
                계정을 삭제하면 목표/챌린지/인증 등 관련 데이터가 함께 삭제될 수 있습니다. 이 작업은 되돌릴 수 없습니다.
              </p>

              {deleteAccountError && (
                <p className="text-xs md:text-sm text-destructive">{deleteAccountError}</p>
              )}

              <div className="flex items-center justify-end space-x-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDeleteConfirmOpen(false)}
                  disabled={isDeletingAccount}
                >
                  취소
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  loading={isDeletingAccount}
                >
                  탈퇴하기
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;